import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { Upload, FileSpreadsheet, ArrowRight, CheckCircle2, Radar } from "lucide-react";
import api, { apiErr, API } from "../lib/api";
import { PageHeader, Loading, Empty, Pill, TempBadge, SectionCard } from "../components/kit";
import { Button } from "../components/ui/button";
import { Select } from "../components/form";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { fullName, titleCase } from "../lib/constants";
import { toast } from "sonner";

export default function Prospecting() {
  return (
    <div className="animate-fade-up">
      <PageHeader title="Prospecting Center" subtitle="Smart lists, FSBO tracking, and spreadsheet import — turn cold data into working leads." testid="prospecting-header" />
      <Tabs defaultValue="lists">
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger value="lists" data-testid="tab-smart-lists" className="data-[state=active]:bg-navy-800 data-[state=active]:text-white">Smart Lists</TabsTrigger>
          <TabsTrigger value="import" data-testid="tab-import" className="data-[state=active]:bg-navy-800 data-[state=active]:text-white">Import Spreadsheet</TabsTrigger>
        </TabsList>
        <TabsContent value="lists" className="mt-5"><SmartLists /></TabsContent>
        <TabsContent value="import" className="mt-5"><ImportWizard /></TabsContent>
      </Tabs>
    </div>
  );
}

function SmartLists() {
  const [lists, setLists] = useState(null);
  const [active, setActive] = useState(null);
  const [items, setItems] = useState(null);
  const nav = useNavigate();

  useEffect(() => { api.get("/smart-lists").then(({ data }) => { setLists(data); if (data[0]) select(data[0].key); }); }, []);
  const select = async (key) => { setActive(key); setItems(null); const { data } = await api.get(`/smart-lists/${key}`); setItems(data.items); };

  if (!lists) return <Loading />;
  return (
    <div className="space-y-6">
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">
      <div className="space-y-2" data-testid="smart-lists-nav">
        {lists.map((l) => {
          const Icon = Icons[titleCase(l.icon).replace(/ /g, "")] || Radar;
          return (
            <button key={l.key} onClick={() => select(l.key)} data-testid={`smart-list-${l.key}`}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${active === l.key ? "bg-navy-800 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-gold/40"}`}>
              <Radar className={`w-4 h-4 ${active === l.key ? "text-gold" : "text-slate-400"}`} />
              <span className="flex-1 text-left">{l.name}</span>
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${active === l.key ? "bg-gold text-navy-900" : "bg-slate-100 text-slate-500"}`}>{l.count}</span>
            </button>
          );
        })}
      </div>
      <div className="lg:col-span-3">
        {!items ? <Loading /> : items.length === 0 ? <Empty title="No matching prospects" /> : (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm divide-y divide-slate-100" data-testid="smart-list-results">
            {items.map((c) => (
              <div key={c.id} className="flex items-center gap-3 px-5 py-3 hover:bg-slate-50 cursor-pointer" onClick={() => nav(`/contacts/${c.id}`)}>
                <div className="w-9 h-9 rounded-full bg-navy-800 text-gold flex items-center justify-center text-xs font-bold shrink-0">{(c.first_name?.[0] || "") + (c.last_name?.[0] || "")}</div>
                <div className="flex-1 min-w-0"><p className="text-sm font-medium text-navy-800 truncate">{fullName(c)}</p><p className="text-xs text-slate-400">{[c.city, c.primary_phone].filter(Boolean).join(" · ")}</p></div>
                {c.verification_status && <Pill className="text-[10px] hidden sm:inline-flex">{titleCase(c.verification_status)}</Pill>}
                <TempBadge value={c.lead_temperature} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
    <PropertySmartLists />
    </div>
  );
}

function PropertySmartLists() {
  const [lists, setLists] = useState(null);
  const [active, setActive] = useState(null);
  const [items, setItems] = useState(null);
  const nav = useNavigate();
  useEffect(() => { api.get("/property-data/smart-lists").then(({ data }) => setLists(data)); }, []);
  const select = async (key) => { setActive(key); setItems(null); const { data } = await api.get(`/property-data/smart-lists/${key}`); setItems(data.items); };
  if (!lists) return null;
  return (
    <div data-testid="property-smart-lists">
      <h3 className="font-display font-semibold text-navy-800 mb-3">Property Intelligence Lists <span className="text-xs text-slate-400 font-normal">(from MLS, public records & FSBO data — test data excluded)</span></h3>
      <div className="flex flex-wrap gap-2 mb-4">
        {lists.map((l) => (
          <button key={l.key} onClick={() => select(l.key)} data-testid={`prop-list-${l.key}`} className={`flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium border transition-colors ${active === l.key ? "bg-navy-800 text-white border-navy-800" : "bg-white border-slate-200 text-slate-600 hover:border-gold/40"}`}>
            {l.name}<span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${active === l.key ? "bg-gold text-navy-900" : "bg-slate-100 text-slate-500"}`}>{l.count}</span></button>
        ))}
      </div>
      {active && (!items ? <Loading /> : items.length === 0 ? <Empty title="No matching properties" /> : (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm divide-y divide-slate-100" data-testid="prop-list-results">
          {items.map((p) => (
            <div key={p.id} className="flex items-center gap-3 px-5 py-3 hover:bg-slate-50 cursor-pointer" onClick={() => nav(`/properties/${p.id}`)}>
              <Icons.Home className="w-4 h-4 text-gold-dark shrink-0" />
              <div className="flex-1 min-w-0"><p className="text-sm font-medium text-navy-800 truncate">{p.address}</p><p className="text-xs text-slate-400">{[p.city, p.primary_source_name, p.days_on_market ? `${p.days_on_market} DOM` : null].filter(Boolean).join(" · ")}</p></div>
              <span className="text-sm font-semibold text-navy-800">{p.asking_price ? `$${Number(p.asking_price).toLocaleString()}` : ""}</span>
              <Pill className="text-[10px]">{titleCase(p.status || "")}</Pill>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function ImportWizard() {
  const [step, setStep] = useState(1);
  const [entity, setEntity] = useState("contacts");
  const [preview, setPreview] = useState(null);
  const [fields, setFields] = useState([]);
  const [mapping, setMapping] = useState({});
  const [result, setResult] = useState(null);
  const [sources, setSources] = useState([]);
  useEffect(() => { api.get("/property-data/sources").then(({ data }) => setSources(data.filter((s) => ["public_records", "mls", "import", "web", "manual"].includes(s.source_type)))).catch(() => {}); }, []);
  const [busy, setBusy] = useState(false);

  const autoMap = (columns, targetFields) => {
    const m = {};
    targetFields.forEach((f) => {
      const norm = f.replace(/_/g, "").toLowerCase();
      const match = columns.find((c) => c.replace(/[^a-z]/gi, "").toLowerCase().includes(norm) || norm.includes(c.replace(/[^a-z]/gi, "").toLowerCase()));
      if (match) m[f] = match;
    });
    return m;
  };

  const upload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const isSource = entity.startsWith("source:");
      const { data: fd2 } = isSource ? await api.get("/property-data/mapping-defaults").then((r) => ({ data: { fields: [...new Set([...r.data.public_record_fields, ...r.data.akinita_fields])] } })) : await api.get(`/import/fields?entity=${entity}`);
      setPreview(data); setFields(fd2.fields); setMapping(autoMap(data.columns, fd2.fields)); setStep(2);
    } catch (err) { toast.error(apiErr(err)); }
    setBusy(false);
  };

  const commit = async () => {
    setBusy(true);
    try {
      if (entity.startsWith("source:")) {
        const { data } = await api.post("/property-data/import/commit", { token: preview.token, source_id: entity.slice(7), mapping });
        setResult({ imported: data.created, updated: data.updated, duplicates: data.duplicates, errors: data.failed, skipped: data.skipped, job_id: data.id, error_rows: data.errors });
      } else { const { data } = await api.post("/import/commit", { token: preview.token, entity, mapping }); setResult(data); }
      setStep(3);
    }
    catch (err) { toast.error(apiErr(err)); }
    setBusy(false);
  };

  const reset = () => { setStep(1); setPreview(null); setResult(null); setMapping({}); };

  return (
    <div className="max-w-3xl">
      <div className="flex items-center gap-2 mb-6">
        {["Upload", "Map Columns", "Complete"].map((s, i) => (
          <div key={s} className="flex items-center gap-2">
            <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${step > i ? "bg-gold text-navy-900" : step === i + 1 ? "bg-navy-800 text-white" : "bg-slate-200 text-slate-400"}`}>{i + 1}</span>
            <span className={`text-sm ${step === i + 1 ? "font-semibold text-navy-800" : "text-slate-400"}`}>{s}</span>
            {i < 2 && <ArrowRight className="w-4 h-4 text-slate-300 mx-1" />}
          </div>
        ))}
      </div>

      {step === 1 && (
        <SectionCard>
          <div className="mb-4"><label className="block text-xs font-semibold text-slate-600 mb-1.5">Import into</label>
            <Select options={[{ value: "contacts", label: "Contacts / Prospects" }, { value: "properties", label: "Properties (legacy quick import)" }, ...sources.map((s) => ({ value: `source:${s.id}`, label: `Property data → ${s.name}${s.environment === "test" ? " (TEST)" : ""}` }))]} value={entity} onChange={setEntity} testid="import-entity" />
            {entity.startsWith("source:") && <p className="text-[11px] text-slate-400 mt-1.5">Rows are matched to existing properties by parcel / address (no duplicates), every source value is preserved for comparison, and nothing is silently discarded — skipped/failed rows are downloadable.</p>}</div>
          <label className="border-2 border-dashed border-slate-200 rounded-xl p-10 flex flex-col items-center justify-center cursor-pointer hover:border-gold transition-colors" data-testid="import-dropzone">
            <FileSpreadsheet className="w-12 h-12 text-slate-300 mb-3" />
            <p className="text-sm font-medium text-navy-800">{busy ? "Reading file…" : "Upload CSV or XLSX"}</p>
            <p className="text-xs text-slate-400 mt-1">FSBO prospecting databases, lead lists, and more</p>
            <input type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={upload} disabled={busy} data-testid="import-file-input" />
          </label>
        </SectionCard>
      )}

      {step === 2 && preview && (
        <SectionCard title={`Map ${preview.row_count} rows · ${preview.filename}`}>
          <p className="text-xs text-slate-500 mb-4">Match each CRM field to a column from your file. Original data is preserved.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[420px] overflow-y-auto pr-2">
            {fields.map((f) => (
              <div key={f}>
                <label className="block text-xs font-medium text-slate-600 mb-1">{titleCase(f)}</label>
                <Select options={[{ value: "", label: "— skip —" }, ...preview.columns.map((c) => ({ value: c, label: c }))]} value={mapping[f] || ""} onChange={(v) => setMapping((m) => ({ ...m, [f]: v }))} testid={`map-${f}`} />
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-5">
            <Button variant="outline" onClick={reset}>Back</Button>
            <Button className="bg-navy-800 text-white" onClick={commit} disabled={busy} data-testid="import-commit-btn">{busy ? "Importing…" : "Import Records"}</Button>
          </div>
        </SectionCard>
      )}

      {step === 3 && result && (
        <SectionCard>
          <div className="flex flex-col items-center py-6 text-center" data-testid="import-summary">
            <CheckCircle2 className="w-14 h-14 text-emerald-500 mb-3" />
            <p className="font-display text-xl font-bold text-navy-800">Import Complete</p>
            <p className="text-sm text-slate-500 mt-1">{result.imported} imported{result.updated != null ? ` · ${result.updated} updated` : ""} · {result.duplicates} duplicates{result.updated != null ? " matched" : " skipped"}{result.skipped != null ? ` · ${result.skipped} skipped` : ""} · {result.errors} failed</p>
            <div className={`grid ${result.updated != null ? "grid-cols-5" : "grid-cols-3"} gap-3 mt-6 w-full max-w-2xl`}>
              {[["Imported", result.imported, "text-emerald-600"], ...(result.updated != null ? [["Updated", result.updated, "text-sky-600"], ["Skipped", result.skipped, "text-slate-500"]] : []), ["Duplicates", result.duplicates, "text-amber-600"], ["Failed", result.errors, "text-red-600"]].map(([l, v, c]) => (
                <div key={l} className="rounded-lg bg-slate-50 border border-slate-100 py-4"><p className={`font-display text-2xl font-bold ${c}`}>{v}</p><p className="text-xs text-slate-500">{l}</p></div>
              ))}
            </div>
            {result.job_id && result.error_rows?.length > 0 && <a href={`${API}/property-data/jobs/${result.job_id}/errors.csv?auth=${localStorage.getItem("gas_token")}`} target="_blank" rel="noreferrer" className="mt-4 text-sm font-semibold text-gold-dark hover:underline" data-testid="download-error-report">Download error report ({result.error_rows.length} rows)</a>}
            <Button className="bg-navy-800 text-white mt-6" onClick={reset}>Import Another</Button>
          </div>
        </SectionCard>
      )}
    </div>
  );
}
